'use client';
import type { ReadonlyURLSearchParams } from 'next/navigation';
import { useSearchParams } from 'next/navigation';
import { useEffect } from 'react';

import { QUERY_TOKEN_PARAM_NAME } from '~constants/token';
import { annals } from '~features/annals/hooks';
import { QUERY_PROMOCODE_PARAM_NAME } from '~features/promocode';
import {
	loadFromSessionStorage,
	saveToSessionStorage,
	ssKeys,
} from '~features/storage';
import { useSession } from '~hooks/useSession';
import { usePathname, useRouter } from '~i18n/navigation';
import { MarkersService } from '~services/markers';
import { NextUrlConstructor } from '~utils/NextUrlConstructor';

export const useInitialRouteHandle = () => {
	const pathname = usePathname();
	const searchParams = useSearchParams();
	const { replace } = useRouter();
	const { userId, isDemoUser } = useSession();

	const annalsDemoVisitor = annals.auth.useSiteVisitDemoUser();

	useEffect(() => {
		const sent = loadFromSessionStorage(ssKeys.ANNALS_SITE_VISIT_SENT, false);
		if (!isDemoUser || sent) return;

		const markers = MarkersService.getMarkersAnnals();
		annalsDemoVisitor({ ...markers, id: userId });
		saveToSessionStorage(ssKeys.ANNALS_SITE_VISIT_SENT, true);
	}, [annalsDemoVisitor, isDemoUser, userId]);

	useEffect(() => {
		const cleanPath = clearParams(pathname, searchParams);
		if (cleanPath.endsWith('?') || cleanPath !== pathname) {
			replace(cleanPath.endsWith('?') ? cleanPath.replace('?', '') : cleanPath);
		}
	}, [pathname, replace, searchParams]);
};

function clearParams(base: string, params: ReadonlyURLSearchParams): string {
	return new NextUrlConstructor(base, params)
		.delete(QUERY_TOKEN_PARAM_NAME)
		.delete(QUERY_PROMOCODE_PARAM_NAME)
		.delete('botId')
		.delete('afid')
		.delete('utm_campaign')
		.delete('utm_source')
		.delete('utm_medium')
		.getUrl();
}

export const InitialRouteHandler = () => {
	useInitialRouteHandle();
	return null;
};
